import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PageHero from '../../components/ui/page-hero';
import TestimonialCard from '../../components/testimonials/TestimonialCard';
import StickyCTA from '../../components/testimonials/StickyCTA';
import { Button } from '../../components/ui/button';
import { Search } from 'lucide-react';

const Testimonials = () => {
  const [testimonialsData, setTestimonialsData] = useState(null);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('/content/testimonials/testimonials.json');
        const data = await response.json();
        setTestimonialsData(data);
        setFilteredTestimonials(data.testimonials);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (!testimonialsData) return;

    let filtered = testimonialsData.testimonials;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(testimonial =>
        testimonial.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.clientCompany.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.testimonial.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.projectType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(testimonial => testimonial.category === selectedCategory);
    }

    // Filter by industry
    if (selectedIndustry !== 'All') {
      filtered = filtered.filter(testimonial => testimonial.clientIndustry === selectedIndustry);
    }

    setFilteredTestimonials(filtered);
  }, [testimonialsData, searchTerm, selectedCategory, selectedIndustry]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading testimonials...</p>
        </div>
      </div>
    );
  }

  if (!testimonialsData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Error loading testimonials. Please try again later.</p>
        </div>
      </div>
    );
  }

  const featuredTestimonials = testimonialsData.testimonials.filter(t => t.featured);
  const regularTestimonials = filteredTestimonials.filter(t => !t.featured);

  return (
    <>
      <Helmet>
        <title>{testimonialsData.meta.title}</title>
        <meta name="description" content={testimonialsData.meta.description} />
        <meta name="keywords" content={testimonialsData.meta.keywords} />
        <meta property="og:title" content={testimonialsData.meta.title} />
        <meta property="og:description" content={testimonialsData.meta.description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={testimonialsData.meta.title} />
        <meta name="twitter:description" content={testimonialsData.meta.description} />
      </Helmet>

      <PageHero
        pageName="Client Testimonials"
        subtitle={testimonialsData.hero.subtitle}
        size="large"
        fullHeight={true}
        enableSpline={true}
      />

      {/* Stats Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">
                {testimonialsData.hero.stats.totalClients}
              </div>
              <div className="text-sm text-muted-foreground">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">
                {testimonialsData.hero.stats.satisfactionRate}
              </div>
              <div className="text-sm text-muted-foreground">Satisfaction Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">
                {testimonialsData.hero.stats.projectsCompleted}
              </div>
              <div className="text-sm text-muted-foreground">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">
                {testimonialsData.hero.stats.yearsExperience}
              </div>
              <div className="text-sm text-muted-foreground">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Testimonials */}
      {featuredTestimonials.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Featured Success Stories
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Discover how leading companies have transformed their businesses with Envael's innovative solutions.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredTestimonials.map((testimonial) => (
                <TestimonialCard
                  key={testimonial.id}
                  testimonial={testimonial}
                  featured={true}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters and Search */}
      <section className="py-8 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search testimonials..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="lg:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {testimonialsData.categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Industry Filter */}
            <div className="lg:w-64">
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                className="w-full px-4 py-3 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {testimonialsData.industries.map((industry) => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-6 flex items-center justify-between">
            <p className="text-muted-foreground">
              Showing {filteredTestimonials.length} of {testimonialsData.testimonials.length} testimonials
            </p>
            {(searchTerm || selectedCategory !== 'All' || selectedIndustry !== 'All') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedIndustry('All');
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* All Testimonials */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {regularTestimonials.length > 0 ? (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  All Client Testimonials
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Read detailed success stories from our diverse client base across various industries.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularTestimonials.map((testimonial) => (
                  <TestimonialCard
                    key={testimonial.id}
                    testimonial={testimonial}
                    featured={false}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No testimonials found
              </h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search criteria or filters to find what you're looking for.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedIndustry('All');
                }}
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Sticky CTA */}
      <StickyCTA ctaData={testimonialsData.stickyCta} />
    </>
  );
};

export default Testimonials;
